import { useCallback } from "react";
import { useApiCall } from "../apiHelpers";

interface CreateEntityTimeslotParams {
    entityId: number;
    channelId: number;
    startHour: string;
    endHour: string;
}

interface UpdateEntityTimeslotParams {
    timeslotId: number;
    startHour: string;
    endHour: string;
}

interface EntityTimeslotResource {
    id: number;
    [key: string]: unknown;
}

const UNKNOWN_ERROR = "An unknown error occurred";

const useEntityTimeslotMutations = () => {
    const { authFetch, run, isLoading, error } = useApiCall();

    const createEntityTimeslot = useCallback(async ({ entityId, channelId, startHour, endHour }: CreateEntityTimeslotParams): Promise<EntityTimeslotResource | null> => {
        const outcome = await run<EntityTimeslotResource>(
            () => authFetch("/ent_entity_timeslots", {
                method: "POST",
                body: JSON.stringify({ channelId, startHour, endHour, entity: `/api/ent_entities/${entityId}` }),
            }),
            UNKNOWN_ERROR,
        );
        return outcome.ok ? outcome.data : null;
    }, [authFetch, run]);

    const updateEntityTimeslot = useCallback(async ({ timeslotId, startHour, endHour }: UpdateEntityTimeslotParams): Promise<EntityTimeslotResource | null> => {
        const outcome = await run<EntityTimeslotResource>(
            () => authFetch(`/ent_entity_timeslots/${timeslotId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/merge-patch+json" },
                body: JSON.stringify({ startHour, endHour }),
            }),
            UNKNOWN_ERROR,
        );
        return outcome.ok ? outcome.data : null;
    }, [authFetch, run]);

    const deleteEntityTimeslot = useCallback(async (timeslotId: number): Promise<boolean> => {
        const outcome = await run(
            () => authFetch(`/ent_entity_timeslots/${timeslotId}`, { method: "DELETE" }),
            UNKNOWN_ERROR,
        );
        return outcome.ok;
    }, [authFetch, run]);

    return { createEntityTimeslot, updateEntityTimeslot, deleteEntityTimeslot, loading: isLoading, error };
};

export default useEntityTimeslotMutations;
